import React, { useState, useCallback, useEffect } from 'react';
import { Search, Loader2, AlertCircle, Settings, Info, ChevronLeft, ChevronRight, ListChecks, X, Download, Sun, Moon, Monitor } from "lucide-react";
import PropertyModal from 'PropertyModal';
import HelpModal from 'HelpModal';
import BulkSearchModal from 'BulkSearchModal';

const SearchApp = () => {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [themePref, setThemePref] = useState(() => localStorage.getItem('theme') || 'system');
  const [systemDark, setSystemDark] = useState(window.matchMedia('(prefers-color-scheme: dark)').matches);
  const isDark = themePref === 'system' ? systemDark : themePref === 'dark';

  const pickTheme = (next) => {
    setThemePref(next);
    if (next === 'system') localStorage.removeItem('theme');
    else localStorage.setItem('theme', next);
  };

  const [isPropertyModalOpen, setIsPropertyModalOpen] = useState(false);
  const [defaultProperties, setDefaultProperties] = useState([]);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [propertyLabels, setPropertyLabels] = useState({});
  const [filterEnabled, setFilterEnabled] = useState(true);  // Default to ON
  
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);

  const [currentSearched, setCurrentSearched] = useState('');  // Track the query we've actually searched for
  
  const [userName, setUserName] = useState('');
  const [authReady, setAuthReady] = useState(false);

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [cachedResults, setCachedResults] = useState({});  // Cache object: { queryKey: { contacts: [], total: 0 } }
  const pageSize = 10;

  const getCacheKey = (query, pageNum, props, filter) => `${query}_${pageNum}_${props.join(',')}_${filter}`;


  const AUTH_SERVICE_URL = process.env.REACT_APP_AUTH_SERVICE_URL || '';

  // Pick up ?token= from URL after OAuth callback, persist to localStorage.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (token) {
      localStorage.setItem('auth_token', token);
      params.delete('token');
      const newUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  const getToken = () => localStorage.getItem('auth_token');

  const authFetch = (url, options = {}) => {
    const token = getToken();
    return fetch(url, {
      ...options,
      headers: {
        ...(options.headers || {}),
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
    });
  };

  const redirectToLogin = () => {
    window.location.href = `${AUTH_SERVICE_URL}/login?redirect_url=${encodeURIComponent(window.location.href)}`;
  };

  // Fetch user info when component mounts
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await authFetch('/api/user');
        if (response.status === 401 || response.status === 403) { redirectToLogin(); return; }
        if (response.ok) {
          const data = await response.json();
          setUserName(data.name);
        }
      } catch (error) {
        console.error('Failed to fetch user info:', error);
      }
      setAuthReady(true);
    };
    fetchUserInfo();
  }, []);

  // Fetch default properties
  useEffect(() => {
    const fetchDefaultProperties = async () => {
      try {
        const response = await authFetch('/api/default-properties');
        if (response.ok) {
          const data = await response.json();
          setDefaultProperties(data.map(prop => prop.name));
          setSelectedProperties(data.map(prop => prop.name));
          // Also update property labels with the defaults
          const labels = {};
          data.forEach(prop => {
            labels[prop.name] = prop.label;
          });
          setPropertyLabels(labels);
        } else {
          console.error('Failed to fetch default properties');
        }
      } catch (error) {
        console.error('Error fetching default properties:', error);
      }
    };

    fetchDefaultProperties();
  }, []);

  // Track the system color-scheme preference so we can follow it when themePref === 'system'.
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => setSystemDark(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);
  
  // Implement debouncing with useEffect
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), 500);
    return () => clearTimeout(timer);
  }, [query]);

  // Separate effect for performing the search
  useEffect(() => {
    const performSearch = async () => {
      if (!debouncedQuery || debouncedQuery.length < 3) {
        setResults([]);
        setError(null);
        setTotal(0);
        return;
      }

      const cacheKey = getCacheKey(debouncedQuery, page, selectedProperties, filterEnabled);
      
      // Check cache first
      if (cachedResults[cacheKey]) {
        setResults(cachedResults[cacheKey].contacts);
        setTotal(cachedResults[cacheKey].total);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Now using relative URL since we're serving from same origin
        const offset = (page - 1) * pageSize;
        const response = await authFetch(
          `/api/search?q=${encodeURIComponent(debouncedQuery)}&properties=${selectedProperties.join(',')}&offset=${offset}&limit=${pageSize}&filter=${filterEnabled}`
        );
        
        if (response.status === 401 || response.status === 403) { redirectToLogin(); return; }
        if (!response.ok) {
          throw new Error(response.status === 429
            ? 'Searching too fast, please wait...'
            : 'Failed to fetch results'
          );
        }

        const data = await response.json();
        setResults(data.contacts);
        setTotal(data.total);
        setCurrentSearched(debouncedQuery);
        // Cache the results
        setCachedResults(prev => ({
          ...prev,
          [cacheKey]: { contacts: data.contacts, total: data.total }
        }));
      } catch (err) {
        setError(err.message);
        setResults([]);
        setCurrentSearched(debouncedQuery);
      } finally {
        setLoading(false);
      }
    };

    performSearch();
  }, [debouncedQuery, selectedProperties, page, filterEnabled]);

  // Reset page when query changes
  useEffect(() => {
    setPage(1);
  }, [debouncedQuery]);

  const totalPages = Math.ceil(total / pageSize);

  // Add an effect just to log selectedProperties changes
  /* useEffect(() => {
    console.log('selectedProperties changed to:', selectedProperties);
  }, [selectedProperties]); */

  const [exporting, setExporting] = useState(null);  // 'csv' | 'xlsx' | null
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [themeMenuOpen, setThemeMenuOpen] = useState(false);

  useEffect(() => {
    if (!exportMenuOpen && !themeMenuOpen) return;
    const onDocClick = (e) => {
      if (exportMenuOpen && !e.target.closest('[data-export-menu]')) setExportMenuOpen(false);
      if (themeMenuOpen && !e.target.closest('[data-theme-menu]')) setThemeMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [exportMenuOpen, themeMenuOpen]);

  const handleExport = async (format) => {
    setExporting(format);
    try {
      let columns;
      let rows;
      let filename;

      if (bulkResults) {
        columns = [
          { key: '_status', label: 'Status' },
          { key: '_term', label: 'Search Term' },
          { key: '_termType', label: 'Term Type' },
          { key: '_reason', label: 'Reason' },
          ...selectedProperties.map(p => ({ key: p, label: propertyLabels[p] || p })),
        ];
        rows = [
          ...bulkResults.found.map(r => ({ ...r, _status: 'Found' })),
          ...bulkResults.notFound.map(r => ({ ...r, _status: 'Not Found' })),
          ...(bulkResults.errors || []).map(r => ({ ...r, _status: 'Error' })),
        ];
        filename = 'alumni-search-bulk';
      } else {
        columns = selectedProperties.map(p => ({ key: p, label: propertyLabels[p] || p }));
        rows = results;
        filename = currentSearched ? `alumni-search-${currentSearched}` : 'alumni-search';
      }

      if (rows.length === 0) {
        setError('Nothing to export.');
        return;
      }

      const resp = await authFetch('/api/export', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ format, filename, columns, rows }),
      });

      if (resp.status === 401 || resp.status === 403) { redirectToLogin(); return; }
      if (!resp.ok) {
        let detail = '';
        try {
          const json = await resp.json();
          detail = json?.detail || JSON.stringify(json);
        } catch (_) {
          detail = await resp.text();
        }
        throw new Error(`Export failed (${resp.status})` + (detail ? `: ${detail}` : ''));
      };

      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filename}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Export failed');
    } finally {
      setExporting(null);
    }
  };

  const canExport = (bulkResults && (bulkResults.found.length + bulkResults.notFound.length) > 0) || results.length > 0;

  const handleSaveProperties = (newProperties) => {
    const updatedProperties = defaultProperties.concat(newProperties);
    console.log(`updatedProperties: ${updatedProperties}`)
    setSelectedProperties(updatedProperties);
    setIsPropertyModalOpen(false);
  };

  if (!authReady) return (
    <div className={`min-h-screen w-full flex flex-col items-center justify-center ${isDark ? 'dark bg-gray-900' : 'bg-gray-50'}`}>
      <img
        src="https://storage.googleapis.com/generic-public-bucket/amuta_8200.png"
        alt="8200 Alumni Association"
        className="h-40 mb-8"
      />
      <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
    </div>
  );

  return (
    <div className={`min-h-screen w-full ${isDark ? 'dark bg-gray-900' : 'bg-gray-50'}`}>
      <div className="w-full px-4 pt-16">

        {/* Logo Section */}
        <div className="flex justify-center mb-2">
          <img 
            src="https://storage.googleapis.com/generic-public-bucket/amuta_8200.png"
            alt="8200 Alumni Association"
            className="h-40" // Bigger logo
          />
        </div>

        {/* Main content container */}
        <div className="max-w-7xl mx-auto">
          {/* User Greeting - same width as search container */}
          {userName && (
            <div className="max-w-2xl mx-auto mb-2">
              <p className={`text-xl ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Welcome back, {userName.split(' ')[0]}! 👋
              </p>
            </div>
          )}

          {/* Search section with constrained width */}
          <div className="max-w-2xl mx-auto relative mt-4"> {/* Narrower container for search */}
            {/* Help and Settings buttons */}
            <div className="mb-4 flex items-center gap-4 flex-wrap">
              <div className="flex-1" />
              <div className="flex items-center gap-4 flex-wrap justify-center">

              {/* Verified Only toggle */}
              <label className="flex items-center gap-1 cursor-pointer">
                <div className={`relative inline-flex h-6 w-11 items-center rounded-full
                  ${isDark 
                    ? 'bg-gray-700 peer-checked:bg-blue-600' 
                    : 'bg-gray-200 peer-checked:bg-blue-600'}`}>
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={filterEnabled}
                    onChange={e => setFilterEnabled(e.target.checked)}
                  />
                  <div className={`w-11 h-6 rounded-full peer 
                    ${isDark 
                      ? 'bg-gray-700 peer-checked:bg-blue-600' 
                      : 'bg-gray-200 peer-checked:bg-blue-600'}
                    peer-focus:outline-none
                    after:content-[''] after:absolute after:top-[2px] after:left-[2px] 
                    after:bg-white after:border-gray-300 after:border after:rounded-full 
                    after:h-5 after:w-5 after:transition-all
                    peer-checked:after:translate-x-full peer-checked:after:border-white`}>
                  </div>
                </div>
                <span className={`text-sm ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}>
                  <b>Verified</b>
                </span>
              </label>

              {/* Bulk Search Button */}
              <button
                onClick={() => setIsBulkModalOpen(true)}
                className={`flex items-center gap-1
                  ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <ListChecks size={16} />
                <span className="text-sm">Bulk</span>
              </button>

              {/* Export menu */}
              <div className="relative" data-export-menu>
                <button
                  onClick={() => canExport && setExportMenuOpen(o => !o)}
                  disabled={!canExport || exporting !== null}
                  title={canExport ? 'Export current view' : 'Nothing to export'}
                  aria-haspopup="menu"
                  aria-expanded={exportMenuOpen}
                  className={`flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed
                    ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  {exporting !== null ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                  <span className="text-sm">Export</span>
                </button>
                {exportMenuOpen && (
                  <div
                    role="menu"
                    className={`absolute right-0 mt-2 min-w-[8rem] rounded-md shadow-lg ring-1 z-20
                      ${isDark
                        ? 'bg-gray-800 ring-gray-700 text-gray-200'
                        : 'bg-white ring-gray-200 text-gray-700'}`}
                  >
                    {['csv', 'xlsx'].map(fmt => (
                      <button
                        key={fmt}
                        role="menuitem"
                        onClick={() => { setExportMenuOpen(false); handleExport(fmt); }}
                        className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2
                          ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                      >
                        <Download size={14} />
                        {fmt.toUpperCase()}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Help Button */}
              <button
                onClick={() => setIsHelpOpen(true)}
                className={`flex items-center gap-1 
                  ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <Info size={16} />
                <span className="text-sm">Help</span>
              </button>

              {/* Customize Columns Button */}
              <button
                onClick={() => setIsPropertyModalOpen(true)}
                className={`flex items-center gap-1
                  ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <Settings size={16} />
                <span className="text-sm">Columns</span>
              </button>
              </div>

              {/* Theme menu — right-aligned with the search bar's rounded edge */}
              <div className="flex-1 flex justify-end pr-6" data-theme-menu>
                <div className="relative">
                  <button
                    onClick={() => setThemeMenuOpen(o => !o)}
                    title={`Theme: ${themePref}`}
                    aria-haspopup="menu"
                    aria-expanded={themeMenuOpen}
                    className={`flex items-center gap-1
                      ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    {themePref === 'system'
                      ? <Monitor size={16} />
                      : themePref === 'dark'
                        ? <Moon size={16} />
                        : <Sun size={16} />}
                    {/* <span className="text-sm">Theme</span> */}
                  </button>
                  {themeMenuOpen && (
                    <div
                      role="menu"
                      className={`absolute right-0 mt-2 min-w-[8rem] rounded-md shadow-lg ring-1 z-20
                        ${isDark
                          ? 'bg-gray-800 ring-gray-700 text-gray-200'
                          : 'bg-white ring-gray-200 text-gray-700'}`}
                    >
                      {[
                        { key: 'system', label: 'System', Icon: Monitor },
                        { key: 'light', label: 'Light', Icon: Sun },
                        { key: 'dark', label: 'Dark', Icon: Moon },
                      ].map(({ key, label, Icon }) => (
                        <button
                          key={key}
                          role="menuitemradio"
                          aria-checked={themePref === key}
                          onClick={() => { setThemeMenuOpen(false); pickTheme(key); }}
                          className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2
                            ${themePref === key ? (isDark ? 'bg-gray-700' : 'bg-gray-100') : ''}
                            ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                        >
                          <Icon size={14} />
                          {label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

            </div>

            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <Search className={`h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
              </div>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Start typing to search..."
                autoFocus
                tabIndex={1}
                className={`w-full pl-10 pr-4 py-3 rounded-full border-2 
                  ${isDark 
                    ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400 focus:border-blue-500' 
                    : 'bg-white border-gray-200 focus:border-blue-500'} 
                  focus:ring-2 focus:ring-blue-200 shadow-lg text-lg transition-all duration-200`}
              />
              {loading && (
                <div className="absolute inset-y-0 right-3 flex items-center">
                  <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />
                </div>
              )}
            </div>
            
          </div>
        </div>

        {error && (
          <div className="max-w-2xl mx-auto"> {/* Error message aligned with search box */}
            <div className={`mt-4 p-4 rounded-lg flex items-center gap-2 
              ${isDark ? 'bg-red-900/50 text-red-300' : 'bg-red-50 text-red-700'}`}>
              <AlertCircle className="h-5 w-5" />
              {error}
            </div>
          </div>
        )}

        {/* Table section with full width */}
        <div className="max-w-7xl mx-auto">
          {bulkResults ? (
            <div className={`mt-6 rounded-lg shadow-lg overflow-hidden
              ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
              <div className={`px-4 py-3 flex items-center justify-between border-b
                ${isDark ? 'border-gray-700 text-gray-200' : 'border-gray-200 text-gray-700'}`}>
                <span className="text-sm font-semibold">
                  Bulk results — Found: {bulkResults.found.length}, Not Found: {bulkResults.notFound.length}, Errors: {(bulkResults.errors || []).length}
                </span>
                <button
                  onClick={() => setBulkResults(null)}
                  className={`flex items-center gap-1 text-sm
                    ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  <X size={16} /> Clear bulk results
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className={isDark ? 'bg-gray-700' : 'bg-gray-50'}>
                    <tr>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider
                        ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                        Search Term
                      </th>
                      {selectedProperties.map(prop => (
                        <th key={prop} className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider
                          ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                          {propertyLabels[prop] || prop}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    {bulkResults.found.length > 0 && (
                      <tr className={isDark ? 'bg-gray-900/40' : 'bg-gray-100'}>
                        <td colSpan={selectedProperties.length + 1}
                          className={`px-6 py-2 text-xs font-semibold uppercase tracking-wider
                            ${isDark ? 'text-green-300' : 'text-green-700'}`}>
                          Found ({bulkResults.found.length})
                        </td>
                      </tr>
                    )}
                    {bulkResults.found.map((row, idx) => (
                      <tr key={`f-${idx}`} className={`transition-colors duration-150
                        ${isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-50 text-gray-900'}`}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono">
                          {row._term}
                          <span className={`ml-2 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            ({row._termType})
                          </span>
                        </td>
                        {selectedProperties.map(prop => (
                          <td key={prop} className="px-6 py-4 whitespace-nowrap text-sm">
                            {row[prop] || ''}
                          </td>
                        ))}
                      </tr>
                    ))}
                    {bulkResults.notFound.length > 0 && (
                      <tr className={isDark ? 'bg-gray-900/40' : 'bg-gray-100'}>
                        <td colSpan={selectedProperties.length + 1}
                          className={`px-6 py-2 text-xs font-semibold uppercase tracking-wider
                            ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
                          Not Found ({bulkResults.notFound.length})
                        </td>
                      </tr>
                    )}
                    {bulkResults.notFound.map((row, idx) => (
                      <tr key={`nf-${idx}`}
                        className={isDark ? 'bg-amber-900/20 text-amber-200' : 'bg-amber-50 text-amber-900'}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono">
                          {row._term}
                          <span className={`ml-2 text-xs ${isDark ? 'text-amber-300/70' : 'text-amber-700/70'}`}>
                            ({row._termType})
                          </span>
                        </td>
                        {selectedProperties.map(prop => (
                          <td key={prop} className="px-6 py-4 whitespace-nowrap text-sm">—</td>
                        ))}
                      </tr>
                    ))}
                    {(bulkResults.errors || []).length > 0 && (
                      <tr className={isDark ? 'bg-gray-900/40' : 'bg-gray-100'}>
                        <td colSpan={selectedProperties.length + 1}
                          className={`px-6 py-2 text-xs font-semibold uppercase tracking-wider
                            ${isDark ? 'text-red-300' : 'text-red-700'}`}>
                          Errors ({bulkResults.errors.length})
                        </td>
                      </tr>
                    )}
                    {(bulkResults.errors || []).map((row, idx) => (
                      <tr key={`e-${idx}`}
                        className={isDark ? 'bg-red-900/30 text-red-200' : 'bg-red-50 text-red-900'}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono">
                          {row._term}
                          <span className={`ml-2 text-xs ${isDark ? 'text-red-300/70' : 'text-red-700/70'}`}>
                            ({row._termType})
                          </span>
                        </td>
                        {selectedProperties.map((prop, i) => (
                          <td key={prop} className="px-6 py-4 whitespace-nowrap text-sm italic">
                            {i === 0 ? (row._reason || 'error') : '—'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className={`mt-6 rounded-lg shadow-lg overflow-hidden
              ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className={isDark ? 'bg-gray-700' : 'bg-gray-50'}>
                    <tr>
                      {selectedProperties.map(prop => (
                        <th key={prop} className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0
                          ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                          {propertyLabels[prop] || prop}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    {results.map((result, idx) => (
                      <tr key={idx} className={`transition-colors duration-150
                        ${isDark
                          ? 'hover:bg-gray-700 text-gray-300'
                          : 'hover:bg-gray-50 text-gray-900'}`}>
                        {selectedProperties.map(prop => (
                          <td key={prop} className="px-6 py-4 whitespace-nowrap text-sm">
                            {result[prop] || ''}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className={`mt-4 px-4 py-3 flex items-center justify-between ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  <div className="text-sm">
                    {total > 0 && (
                      <span>
                        Showing {((page - 1) * pageSize) + 1}-{Math.min(page * pageSize, total)} of {total} results
                      </span>
                    )}
                  </div>

                  {totalPages > 1 && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1 || loading}
                        className={`p-2 rounded-lg transition-colors
                          ${isDark
                            ? 'hover:bg-gray-700 disabled:text-gray-600'
                            : 'hover:bg-gray-100 disabled:text-gray-300'}
                          disabled:cursor-not-allowed`}
                      >
                        <ChevronLeft size={20} />
                      </button>

                      <span className="px-4 py-2">
                        Page {page} of {totalPages}
                      </span>

                      <button
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages || loading}
                        className={`p-2 rounded-lg transition-colors
                          ${isDark
                            ? 'hover:bg-gray-700 disabled:text-gray-600'
                            : 'hover:bg-gray-100 disabled:text-gray-300'}
                          disabled:cursor-not-allowed`}
                      >
                        <ChevronRight size={20} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {!bulkResults && query.length >= 3 && !loading && results.length === 0 && !error &&
          query === currentSearched && (
          <div className={`mt-6 p-4 text-center rounded-lg shadow
            ${isDark
              ? 'bg-gray-800 text-gray-300'
              : 'bg-white text-gray-500'}`}
          >
            No results found
          </div>
        )}

        {/* Modals */}
        <PropertyModal
          isOpen={isPropertyModalOpen}
          onClose={() => setIsPropertyModalOpen(false)}
          onSave={handleSaveProperties}
          selectedProperties={selectedProperties}
          defaultProperties={defaultProperties}
          isDark={isDark}
          authFetch={authFetch}
        />

        <HelpModal
          isOpen={isHelpOpen}
          onClose={() => setIsHelpOpen(false)}
          isDark={isDark}
        />

        <BulkSearchModal
          isOpen={isBulkModalOpen}
          onClose={() => setIsBulkModalOpen(false)}
          onResults={setBulkResults}
          selectedProperties={selectedProperties}
          isDark={isDark}
          authFetch={authFetch}
        />
      </div>
    </div>
  );
};

export default SearchApp;